import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import { Pencil, Trash2, MoreHorizontal, Ban, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PagePagination } from '@/components/shared';
import { ROW_MENU_CONTENT, ROW_MENU_ITEM, ROW_MENU_ITEM_DANGER } from '@/components/layouts/menu-styles';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import type { CSCategory } from '@/types';

interface ActionsCellProps {
  intent: CSCategory;
  onEdit: (intent: CSCategory) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (intent: CSCategory) => void;
}

/** The row menu: Edit, Disable / Enable, Delete. */
export function ActionsCell({ intent, onEdit, onDelete, onToggleEnabled }: ActionsCellProps) {
  const isEnabled = intent.isEnabled !== false;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-label='Open menu'
          className='flex size-7 cursor-pointer items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100'
        >
          <MoreHorizontal className='size-3.5' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className={ROW_MENU_CONTENT}>
        <DropdownMenuItem onClick={() => onEdit(intent)} className={ROW_MENU_ITEM}>
          <Pencil />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onToggleEnabled(intent)} className={ROW_MENU_ITEM}>
          {isEnabled ? <Ban /> : <CheckCircle2 />}
          {isEnabled ? 'Disable' : 'Enable'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDelete(intent.id)} className={cn(ROW_MENU_ITEM, ROW_MENU_ITEM_DANGER)}>
          <Trash2 />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface CSIntentsTableProps {
  intents: CSCategory[];
  isLoading?: boolean;
  onEdit: (intent: CSCategory) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (intent: CSCategory) => void;
  /** Show the "Customized" tag — only meaningful for a BU's own copies, not templates. */
  showCustomizedTag?: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

export function CSIntentsTable({
  intents,
  isLoading,
  onEdit,
  onDelete,
  onToggleEnabled,
  showCustomizedTag = false,
  total,
  page,
  limit,
  totalPages,
  onPageChange,
  onLimitChange,
}: CSIntentsTableProps) {
  const columns = useMemo<ColumnDef<CSCategory>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <div className='font-medium'>{row.getValue('name') || '—'}</div>
      },
      {
        accessorKey: 'id',
        header: 'ID',
        cell: ({ row }) => <div className='font-mono text-sm text-muted-foreground'>{row.getValue('id')}</div>
      },
      {
        accessorKey: 'definition',
        header: 'Definition',
        cell: ({ row }) => {
          const definition: string = row.getValue('definition') || '';
          if (!definition) return <span className='text-muted-foreground'>—</span>;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='max-w-[300px] truncate text-sm text-muted-foreground cursor-default'>
                  {definition}
                </div>
              </TooltipTrigger>
              <TooltipContent className='max-w-sm whitespace-pre-wrap text-xs leading-relaxed'>
                {definition}
              </TooltipContent>
            </Tooltip>
          );
        }
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) => <div>{row.getValue('category')}</div>
      },
      {
        accessorKey: 'subcategory',
        header: 'Subcategory',
        cell: ({ row }) => <div className='text-muted-foreground'>{row.getValue('subcategory') || '—'}</div>
      },
      {
        accessorKey: 'isEnabled',
        header: 'Status',
        cell: ({ row }) => {
          const isEnabled = row.original.isEnabled !== false;
          const isCustomized = showCustomizedTag && row.original.isCustomized;
          return (
            <div className='flex items-center gap-2'>
              {isEnabled ? (
                <Badge variant='outline' className='text-green-600 border-green-400 bg-green-50'>
                  Enabled
                </Badge>
              ) : (
                <Badge variant='outline' className='text-muted-foreground border-border bg-muted'>
                  Disabled
                </Badge>
              )}
              {isCustomized && (
                <Badge variant='outline' className='text-orange-600 border-orange-400 bg-orange-50'>
                  Customized
                </Badge>
              )}
            </div>
          );
        }
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <ActionsCell intent={row.original} onEdit={onEdit} onDelete={onDelete} onToggleEnabled={onToggleEnabled} />
        )
      }
    ],
    [onEdit, onDelete, onToggleEnabled, showCustomizedTag]
  );

  const table = useReactTable({
    data: intents,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className='space-y-4'>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-48 text-center'>
                <div className='flex flex-col items-center justify-center gap-2'>
                  <Spinner className='size-6' />
                  <span className='text-sm text-muted-foreground'>Loading intents...</span>
                </div>
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center text-muted-foreground'>
                No intents found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <PagePagination
        noun='intent'
        total={total}
        page={page}
        limit={limit}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />
    </div>
  );
}
